import React, { useContext, useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  MenuItem,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Select,
  CircularProgress as MuiCircularProgress
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  Pie,
  PieChart,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  LineChart,
  Line,
  Tooltip as RechartsTooltip,
  ResponsiveContainer
} from "recharts";
import axios from 'axios';
import { AuthContext } from "..";
import { color, fontFamily, fontWeight } from "@mui/system";

export default function Dashboard() {
  const [todayFollowups, setTodayFollowups] = useState([]);
  const [loadingFollowups, setLoadingFollowups] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [leadsData, setLeadsData] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [totaltasks, setTotaltasks] = useState(0);
  const [clients, setClients] = useState([]);
  const [trendStats, setTrendStats] = useState([]);
  const [loadingTrends, setLoadingTrends] = useState(true);
  const [salesDataByYear, setSalesDataByYear] = useState({});


  const { companyid, token } = useContext(AuthContext);

  const dashboardleadsdata = () => {
    axios.get(`${process.env.REACT_APP_API_LINKS}/dashboard/leadsdata`,
      {
        headers: {
          Authorization: `bearer ${token}`
        },
        withCredentials: true
      }
    ).then((res) => {
      const data = res.data.data;
      if (Array.isArray(data)) {
        setLeadsData(data);
      } else {
        console.error('Service data is not in expected format:', res.data);
        setLeadsData([]);
      }
    }).catch(err => {
      console.error("Error fetching leads data:", err);
    });
  };

  const dashboardtasksdata = () => {
    axios.get(`${process.env.REACT_APP_API_LINKS}/dashboard/tasksdata`,
      {
        headers: {
          Authorization: `bearer ${token}`
        },
        withCredentials: true
      }
    ).then((res) => {
      const data = res.data.data;
      const total = res.data.row?.total ?? 0;
      setTotaltasks(total);
      if (Array.isArray(data)) {
        setTasks(data);
      } else {
        console.error('Service data is not in expected format:', res.data);
        setTasks([]);
      }
    }).catch(err => {
      console.error("Error fetching tasks:", err);
    });
  };

  const dashboardclientssdata = () => {
    axios.get(`${process.env.REACT_APP_API_LINKS}/dashboard/clientsdata`,
      {
        headers: {
          Authorization: `bearer ${token}`
        },
        withCredentials: true
      }
    ).then((res) => {
      const data = res.data.clients;
      if (Array.isArray(data)) {
        setClients(data);
      } else {
        console.error('Service data is not in expected format:', res.data);
        setClients([]);
      }
    }).catch(err => {
      console.error("Error fetching clients:", err);
    });
  };

  const fetchTrendStats = async () => {
    setLoadingTrends(true);
    try {
      const resp = await axios.get(`${process.env.REACT_APP_API_LINKS}/dashboard/sales-trends`,
        {
          headers: {
            Authorization: `bearer ${token}`
          },
          withCredentials: true
        }
      );
      if (resp.data.success && Array.isArray(resp.data.data)) {
        setTrendStats(resp.data.data);
      } else {
        console.error("Unexpected response for trends:", resp.data);
      }
    } catch (err) {
      console.error("Error fetching trend stats:", err);
    } finally {
      setLoadingTrends(false);
    }
  };


  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const fetchSalesData = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_LINKS}/dashboard/salesdata`,
        {
          headers: {
            Authorization: `bearer ${token}`
          },
          withCredentials: true
        }
      );
      if (res.data.success) {
        const rawData = res.data.data;

        const formattedData = {};
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();

        Object.keys(rawData).forEach((year) => {
          const yearData = rawData[year];

          formattedData[year] = months
            .map((m, index) => {
              const found = yearData.find((d) => d.month === m);
              return {
                month: m,
                sales: found ? found.sales : 0,
              };
            })
            .filter((_, index) => {
              if (Number(year) === currentYear) {
                return index <= currentMonth;
              }
              return true;
            });
        });

        setSalesDataByYear(formattedData);
      }
    } catch (err) {
      console.error("Error fetching sales data:", err);
    }
  };

  const fetchFollowups = async () => {
    try {
      setLoadingFollowups(true);
      const res = await axios.get(`${process.env.REACT_APP_API_LINKS}/dashboard/followups`,
        {
          headers: {
            Authorization: `bearer ${token}`
          },
          withCredentials: true
        }
      );
      if (res.status === 200) {
        setTodayFollowups(res.data.data);
      } else {
        console.error('Unexpected followups response:', res.data);
        setTodayFollowups([]);
      }
    } catch (err) {
      console.error('Error fetching followups:', err);
      setTodayFollowups([]);
    } finally {
      setLoadingFollowups(false);
    }
  };





  useEffect(() => {
    dashboardleadsdata();
    dashboardtasksdata();
    dashboardclientssdata();
    fetchTrendStats();
    fetchFollowups();
    fetchSalesData();
  }, [companyid]);

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        paddingLeft: { xs: 2, sm: 3, md: 4 },
        paddingRight: { xs: 2, sm: 3, md: 4 },
        backgroundColor: '#ffffff',
        minHeight: 'calc(100vh - 64px)',
        borderRadius: 2,
        boxShadow: { xs: 'none', sm: '0px 0px 5px rgba(187, 222, 241, 0.9)' }
      }}
    >
      <Box paddingTop={1} marginTop={2}>
        {/* Sales + Clients Row */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "3fr 1fr", lg: '3fr 1fr' } }}>
          {/* Sales (yearly) */}
          <Card
            sx={{
              width: "100%",
              maxWidth: 850,
              boxShadow: "0px 0px 5px #888",
              height: { xs: "auto", md: '100%' },
              mr: 2
            }}
          >
            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, height: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2"
                  sx={{ fontSize: { xs: "1rem", sm: "1.1rem" }, whiteSpace: "normal" }}
                >
                  Yearly Sales
                </Typography>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  size="small"
                >
                  {Object.keys(salesDataByYear).map((year) => (
                    <MenuItem key={year} value={Number(year)}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ width: "100%", height: { xs: 200, sm: 220, md: 240 } }}>
                <ResponsiveContainer>
                  <LineChart
                    data={salesDataByYear[selectedYear]}
                    margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontFamily: 'Open Sans', fontSize: 12, fontWeight: 500, fill: '#333' }} />
                    <YAxis
                      tickFormatter={(value) => `${value / 1000}K`}
                      tick={{ fontFamily: 'Open Sans', fontSize: 12, fontWeight: 500, fill: '#333' }}
                    />
                    <RechartsTooltip sx={{ fontFamily: 'Open Sans' }} />
                    <Line
                      type="monotone"
                      dataKey="sales"
                      stroke="#63B3FF"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          {/* Clients */}
          <Card
            sx={{
              width: { md: "97%" },
              height: { xs: "auto", md: '100%' },
              ml: { lg: 1, md: 1 },
              mr: { lg: -3, md: -3 },
              boxShadow: "0px 0px 5px #888",
              display: "flex",
              flexDirection: "column",
              mt: { xs: 2, md: 0, lg: 0 }
            }}
          >
            <CardContent sx={{ flexGrow: 1, p: 2, fontFamily: 'Open Sans' }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle1" sx={{ whiteSpace: "normal" }}>Clients</Typography>
                <Link to="/clients"><Button size="small">See all</Button></Link>
              </Box>
              <List dense>
                {clients.map((client, index) => (
                  <ListItem key={client.client_name ?? index} divider>
                    <ListItemText sx={{ fontFamily: 'Open Sans' }}
                      primary={<Typography sx={{ fontFamily: 'Open Sans', fontSize: '14px' }}>{`${index + 1}. ${client.client_name}`}</Typography>}
                      secondary={<Typography sx={{ fontFamily: 'Open Sans', fontSize: '12px' }}>{`Sold: ${(client.amount_to_pay).toFixed(0)}`}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Sales & Billing Overview (dynamic) */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ whiteSpace: "normal", fontFamily: 'Open Sans' }}>Sales & Billing Overview</Typography>
          </Box>

          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 2
          }}>
            {loadingTrends ? (
              // optionally show loader while fetching
              <Box sx={{ gridColumn: "1 / -1", textAlign: "center" }}>
                <MuiCircularProgress />
              </Box>
            ) : (
              trendStats.map((stat) => (
                <Card key={stat.title} sx={{ p: 2, boxShadow: "0px 0px 5px #888" }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ whiteSpace: "normal", fontFamily: 'Open Sans' }}>
                    {stat.title}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1, color: stat.color }}>
                    {stat.value}
                  </Typography>
                  <Box sx={{ height: 50, mt: 1, width: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={stat.lineData ?? []}
                      // if lineData not given, this chart will render empty
                      >
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke={stat.color}
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                  <Typography variant="caption"
                    sx={{ color: stat.trendColor, fontWeight: "bold", fontFamily: 'Open Sans' }}
                  >
                    {stat.trend}
                  </Typography>
                </Card>
              ))
            )}
          </Box>
        </Box>

        {/* Tasks */}
        <Box sx={{ mt: 2, pt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, fontFamily: 'Open Sans' }}>
            <Typography variant="subtitle1" sx={{ fontFamily: 'Open Sans' }}>Tasks</Typography>
            <Typography variant="subtitle1" sx={{ fontFamily: 'Open Sans' }}>Total: <b>{totaltasks}</b></Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(auto-fit, minmax(250px, 1fr))", md: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            {tasks.map((task) => {
              const percentage = totaltasks > 0
                ? Math.round((task.value / totaltasks) * 100)
                : 0;

              return (
                <Card
                  key={task.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderRadius: 2,
                    px: 1,
                    py: 1,
                    height: 90,
                    boxShadow: "0px 0px 5px #888",
                    flex :1
                  }}
                >
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={60}
                      sx={{ color: task.bgColor, fontFamily: 'Open Sans' }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={percentage}
                      size={60}
                      sx={{ color: task.color, position: "absolute", left: 0, fontFamily: 'Open Sans' }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'Open Sans' }}>
                        {percentage}%
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ textAlign: "center", flex: 1, whiteSpace: "normal", fontFamily: 'Open Sans' }}
                  >
                    {task.label}
                  </Typography>
                </Card>
              );
            })}
          </Box>
        </Box>

        {/* Leads + Attendance + Follow Ups */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "2fr 1fr", lg: "3fr 1fr" },
            gap: 3,
            mt: 3
          }}
        >
          {/* Leads */}
          <Card
            sx={{
              width: "100%",
              boxShadow: "0px 0px 5px #888",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <CardContent
              sx={{
                p: { xs: 1.5, sm: 2, md: 2.5 },
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  textAlign: "left",
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.15rem" },
                  fontFamily: 'Open Sans'
                }}
              >
                Leads
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: 220, sm: 240, md: 260 },
                    overflowX: 'auto',
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                >
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                    minWidth={leadsData.length * 50}
                  >
                    <BarChart
                      data={leadsData}
                      margin={{ top: 10, bottom: 10 }}
                      barSize={30}
                      style={{ backgroundColor: "transparent", fontFamily: 'Open Sans' }}
                    >
                      <XAxis axisLine={false} dataKey="name" tick={false} sx={{ fontFamily: 'Open Sans' }} />
                      <YAxis type="number" tick={{ fontSize: "0.75rem", fontFamily: 'Open Sans' }} />
                      <RechartsTooltip
                        formatter={(value, name) => [`${value} Leads`, name]}
                        contentStyle={{
                          fontSize: "0.85rem",
                          backgroundColor: "#f0f9ff",
                          border: "none",
                          borderRadius: 3,
                          boxShadow: "none",
                          fontFamily: 'Open Sans'
                        }}
                        cursor={{ fill: "transparent" }}
                      />
                      <Bar dataKey="value" radius={[0, 0, 0, 0]}>
                        {leadsData.map((entry, index) => {
                          const isHovered = hoveredIndex === index;
                          return (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.color}
                              onMouseEnter={() => setHoveredIndex(index)}
                              onMouseLeave={() => setHoveredIndex(null)}
                              style={{
                                cursor: "pointer",
                                transformOrigin: "bottom",
                                clipPath: isHovered
                                  ? "inset(100% 0 0 0)"
                                  : "inset(0 0 0 0)",
                                animation: isHovered
                                  ? "revealBar 0.6s ease forwards"
                                  : "none",
                                fontFamily: 'Open Sans'
                              }}
                            />
                          );
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Custom Legend */}
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                >
                  {leadsData.map((entry, index) => (
                    <Box
                      key={`legend-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1,
                        flex: "0 0 auto",
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: entry.color,
                          borderRadius: "2px",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.75rem", fontFamily: 'Open Sans' }}
                      >
                        {entry.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Follow Ups */}
          <Card
            sx={{
              boxShadow: "0px 0px 5px #888",
              display: "flex",
              flexDirection: "column",
              height: "100%"
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontSize: "1.1rem",
                  whiteSpace: "normal",
                  fontFamily: 'Open Sans'
                }}
              >
                Today's Follow Ups
              </Typography>
              <List>
                {todayFollowups.length === 0 &&
                  <Box sx={{ textAlign: "center", py: 2, width: "100%" }}>
                    <img
                      src="no data found.png"
                      alt="No followups"
                      style={{
                        maxWidth: "150px", opacity: 1, filter: 'drop-shadow(0px 0px 10px rgba(158, 153, 153, 0.5))', fontFamily: 'Open Sans'
                      }}
                    />
                    <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", fontFamily: 'Open Sans' }}>
                      No follow ups today
                    </Typography>
                  </Box>
                }
                {todayFollowups.map((cust, index) => (
                  <ListItem key={index} sx={{ borderBottom: "1px solid #eee" }}>
                    <ListItemText
                      primary={cust.name}
                      secondary={cust.phone}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    {/* <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      component="a"
                      href={`tel:${cust.phone}`}
                      sx={{ fontFamily: 'Open Sans' }}
                    >
                      Call
                    </Button> */}
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container >
  );
}
